import React, { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";

const Field = ({ label, name, type = "text", value, onChange, testid }) => (
  <label className="block group" data-testid={`field-${name}`}>
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
    <input
      type={type}
      name={name}
      required
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="mt-2 w-full bg-transparent border-b border-neutral-300 py-3 text-lg text-black placeholder:text-neutral-300 focus:outline-none focus:border-black transition-colors"
    />
  </label>
);

const WEB3FORMS_ACCESS_KEY = "34e94f88-7eb3-4f5b-87c5-887f3ef62f65";

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "sending") return;

    const formEl = e.currentTarget;
    const fd = new FormData(formEl);

    // Honeypot — if filled, abort (bot detected)
    if (fd.get("botcheck")) {
      return;
    }

    fd.append("access_key", WEB3FORMS_ACCESS_KEY);
    fd.append("subject", `Cerere nouă de pe site Castel Construct (${lang.toUpperCase()})`);
    fd.append("from_name", "Castel Construct — Site Web");
    const emailVal = fd.get("email");
    if (emailVal) fd.append("replyto", String(emailVal).trim());

    setStatus({ state: "sending", message: "" });

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus({ state: "success", message: t.contact.form.success });
        setForm({ name: "", email: "", phone: "", message: "" });
        setTimeout(() => setStatus({ state: "idle", message: "" }), 6000);
      } else {
        setStatus({ state: "error", message: data.message || "Eroare. Încearcă din nou." });
      }
    } catch (err) {
      setStatus({ state: "error", message: "Eroare de rețea. Încearcă din nou." });
    }
  };

  const isSending = status.state === "sending";
  const buttonLabel =
    status.state === "success"
      ? t.contact.form.success
      : isSending
      ? "..."
      : t.contact.form.submit;

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-[var(--bg-secondary)]">
      <div className="container-page">
        <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-10">
          <span className="w-8 h-px bg-neutral-400" />
          {t.contact.eyebrow}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: title + info */}
          <div className="lg:col-span-6">
            <h2 data-testid="contact-title" className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02]">
              {t.contact.title}
            </h2>
            <p className="mt-6 max-w-md text-lg text-neutral-600 leading-relaxed">{t.contact.lead}</p>

            <div className="mt-14 space-y-8">
              <div data-testid="contact-info-phone">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.info.phone}</div>
                <a href="tel:+37368257835" className="block mt-2 font-display text-2xl md:text-3xl hover:text-[var(--accent)] transition-colors">
                  +373 68 257 835
                </a>
              </div>
              <div data-testid="contact-info-email">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.info.email}</div>
                <a href="mailto:castelconstructsrl@gmail.com" className="block mt-2 font-display text-2xl md:text-3xl hover:text-[var(--accent)] transition-colors break-all">
                  castelconstructsrl@gmail.com
                </a>
              </div>
              <div data-testid="contact-info-address">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.info.address}</div>
                <div className="mt-2 font-display text-2xl md:text-3xl leading-tight">MD-2084, Strada Chișinăului 62<br />Cricova, mun. Chișinău</div>
              </div>
              <div data-testid="contact-info-hours">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.info.hours}</div>
                <div className="mt-2 text-lg">{t.contact.info.hoursValue}</div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-6">
            <form onSubmit={onSubmit} data-testid="contact-form" className="bg-white p-8 md:p-12 border border-[var(--border)] space-y-8">
              <Field label={t.contact.form.name} name="name" value={form.name} onChange={onChange} testid="input-name" />
              <Field label={t.contact.form.email} name="email" type="email" value={form.email} onChange={onChange} testid="input-email" />
              <Field label={t.contact.form.phone} name="phone" type="tel" value={form.phone} onChange={onChange} testid="input-phone" />
              <label className="block" data-testid="field-message">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.form.message}</span>
                <textarea
                  name="message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  data-testid="input-message"
                  className="mt-2 w-full bg-transparent border-b border-neutral-300 py-3 text-lg text-black resize-none focus:outline-none focus:border-black transition-colors"
                />
              </label>

              {/* Honeypot — hidden from real users */}
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                style={{ display: "none" }}
                aria-hidden="true"
              />

              <button
                type="submit"
                disabled={isSending}
                data-testid="contact-submit"
                className="group w-full bg-black text-white py-5 flex items-center justify-between px-6 hover:bg-[var(--accent)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{buttonLabel}</span>
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </button>

              {status.state === "error" && (
                <div data-testid="contact-error" className="text-sm text-red-600 font-mono">
                  {status.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
